import React, { useEffect, useRef, useState } from 'react';
import { Region } from '../../types/orderTypes';
import fetchLineHaulSort, {
  LineHaulSortListEntry,
} from '../../fetch/fetchLineHaulSort';
import { ReqEnum } from '../../const/requestStatus';
import { Loader } from '../../components/Loader/Loader';
import RegionTableBar from '../../components/RegionTableBar/RegionTableBar';
import LineHaulScannerTable from '../../components/LineHaulScannerTable/LineHaulScannerTable';

import './LineHaulSortList.scss';
import {
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from '../../utils/pusher';
import Button from '../../components/buttons/Button';
import { HubSortListOrderEntry } from '../../hooks/useFetchHubSortList';
import Swal from 'sweetalert2';
import { LinehaulPresortBar } from './LinehaulPresortBar';
import markPresortedForLinehaul from '../../fetch/markPresortedForLinehaul';
import { CheckboxComponent } from '../../ReusableComponents/Checkbox/CheckboxComponent';

interface Props {
  startRegion: Region;
}

function orderFilter(
  order: HubSortListOrderEntry,
  excludedSenders: Set<string>,
  showCompleted: boolean
): boolean {
  return (
    !excludedSenders.has(order.order.senderName) &&
    (showCompleted || order.scannedParcelCount !== order.preciseParcelCount)
  );
}

export function LineHaulSortList({ startRegion }: Props): JSX.Element {
  const reloadTimeout = useRef<number | undefined>(undefined);
  const queuedNotifications = useRef<boolean>(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [excludedSenders, setExcludedSenders] = useState<Set<string>>(
    new Set()
  );
  const [reqState, setReqState] = useState<ReqEnum>(ReqEnum.INIT);
  const [lineHaulSortList, setLineHaulSortList] = useState<
    LineHaulSortListEntry[]
  >([]);

  useEffect(() => {
    subscribeToChannel('admin-hubscans', PusherEvent.HUBSORT_UPDATE, event => {
      if (!!!reloadTimeout.current) {
        fetchLineHaulSort(startRegion).then(list => setLineHaulSortList(list));
        reloadTimeout.current = window.setTimeout(() => {
          if (queuedNotifications.current) {
            fetchLineHaulSort(startRegion).then(list =>
              setLineHaulSortList(list)
            );
            queuedNotifications.current = false;
          }
          reloadTimeout.current = undefined;
        }, 10000);
      } else {
        queuedNotifications.current = true;
      }
    });
    return function () {
      unsubscribeFromChannel('admin-hubscans');
    };
  }, [startRegion]);

  useEffect(() => {
    setReqState(ReqEnum.PENDING);
    async function fetchSortData() {
      try {
        const lineHaulSortList = await fetchLineHaulSort(startRegion);
        setLineHaulSortList(lineHaulSortList);
        setReqState(ReqEnum.SUCCESS);
      } catch (error) {
        setReqState(ReqEnum.ERROR);
      }
    }
    fetchSortData().then();
  }, [startRegion]);

  const markAsPresorted = (senderName: string, orderIds: string[]) => {
    Swal.fire({
      title: 'Confirm presorted orders',
      text: `Do you want to confirm ${orderIds.length} orders from ${senderName} as presorted?`,
      icon: 'warning',
      showCancelButton: true,
    }).then(result => {
      if (result.value) {
        markPresortedForLinehaul(startRegion, orderIds);
      }
    });
  };

  const senderNames = [].concat(
    ...lineHaulSortList.map(scanningItem => {
      return scanningItem.orders.map(item => item.order.senderName);
    })
  );
  const allSenders = new Set(senderNames);

  if (reqState === ReqEnum.INIT || reqState === ReqEnum.PENDING) {
    return <Loader type="tile" />;
  }

  const filteredSortList = lineHaulSortList.map(entry => {
    return {
      ...entry,
      orders: entry.orders.filter(order =>
        orderFilter(order, excludedSenders, showCompleted)
      ),
    };
  });

  return (
    <div className="linehaul-sort-list">
      <div className="linehaul-sort-list__filters">
        <div className="linehaul-sort-list__filters__header">
          Filter senders:{' '}
          <Button
            title="All"
            type="slim"
            onClick={() => setExcludedSenders(new Set())}
          />
          {'  '}
          <Button
            title="None"
            type="slim"
            onClick={() => setExcludedSenders(allSenders)}
          />
        </div>
        <div>
          {Array.from(allSenders).map(senderName => {
            return (
              <CheckboxComponent
                key={senderName}
                checked={!excludedSenders.has(senderName)}
                handleChange={() => {
                  const updatedSenders = new Set(excludedSenders);
                  if (excludedSenders.has(senderName)) {
                    updatedSenders.delete(senderName);
                    setExcludedSenders(updatedSenders);
                  } else {
                    updatedSenders.add(senderName);
                    setExcludedSenders(updatedSenders);
                  }
                }}
                name={senderName}
                label={senderName}
              />
            );
          })}
        </div>
        <CheckboxComponent
          checked={showCompleted}
          handleChange={() => setShowCompleted(!showCompleted)}
          name="show-completed"
          label="Show completed orders"
        />
      </div>
      {lineHaulSortList.map(entry => (
        <div key={entry.targetRegion}>
          <RegionTableBar region={entry.targetRegion} orders={entry.orders} />
          <LinehaulPresortBar
            listEntry={entry}
            markAsPresorted={(senderName, orderIds) =>
              markAsPresorted(senderName, orderIds)
            }
          />
          <LineHaulScannerTable
            orders={
              filteredSortList.find(
                filteredEntry =>
                  filteredEntry.targetRegion === entry.targetRegion
              ).orders
            }
          />
        </div>
      ))}
    </div>
  );
}
